import axios, { AxiosRequestConfig } from 'axios';
import React, { createContext } from 'react';
import CancellationsApi from '../api/CancellationsApi';
import { ProductConfigurationResponse } from '../api/ConfigApi';
import { handleUnauthorizedResponse } from '../api/http';
import PolicyApi from '../api/PolicyApi';
import ProductApi from '../api/ProductApi';
import { errorToast } from '../components/Toast';
import {
	CancellationResponse,
	CancellationUpdateRequest,
	ReinstatementResponse,
	ReinstatementUpdateRequest,
} from '../types/cancellations';
import { GraceLapseReinstatementResponse, Policy2 } from '../types/policy';
import { getDateErrors, getPolicyConfigVersion } from '../util/util';
import { AuthContext, AuthData } from './auth';
import { ErrorContext, ErrorData } from './ErrorProvider';

export const CancellationContext = createContext<CancellationData>(null);

export const useCancellationContext = () =>
	React.useContext(CancellationContext);

export function withCancellationContext<T>(Component: any) {
	return (props: Omit<T, 'errorData' | 'cancellationData' | 'authData'>) => (
		<ErrorContext.Consumer>
			{(errorData) => (
				<CancellationContext.Consumer>
					{(cancellationData) => (
						<AuthContext.Consumer>
							{(authData) => (
								<Component
									{...props}
									authData={authData}
									cancellationData={cancellationData}
									errorData={errorData}
								/>
							)}
						</AuthContext.Consumer>
					)}
				</CancellationContext.Consumer>
			)}
		</ErrorContext.Consumer>
	);
}

export interface CancellationData {
	cancellationUpdateObj: CancellationUpdateRequest;
	reinstatementUpdateObj: ReinstatementUpdateRequest;
	policy: Policy2;
	graceLapse?: GraceLapseReinstatementResponse[];
	productConfig: ProductConfigurationResponse;
	selectedCancellation?: CancellationResponse;
	loadCancellation?: (
		cancellationId: string,
		policyId: string,
		config?: AxiosRequestConfig<any>,
	) => Promise<boolean>;
	setCancellationUpdateObj: (
		cancellationUpdateObj: CancellationUpdateRequest,
	) => Promise<CancellationData>;
	setSelectedCancellation: (
		selectedCancellation: CancellationResponse,
	) => Promise<CancellationData>;
	setGraceLapse: (
		graceLapse: GraceLapseReinstatementResponse[],
	) => Promise<CancellationData>;
	setReinstatementUpdateObj: (
		reinstatementUpdateObj: ReinstatementUpdateRequest,
	) => Promise<CancellationData>;
	setPolicy: (policy: Policy2) => Promise<CancellationData>;
	setProductConfig: (
		productConfig: ProductConfigurationResponse,
	) => Promise<CancellationData>;
	loadData: (
		policyId: string,
		cancellationId: string,
		config?: AxiosRequestConfig<any>,
	) => Promise<boolean>;
	clear: () => Promise<CancellationData>;
	setLoading?: (loading: boolean) => void;
	isReinstatementUpdateObjValid?: () => boolean;
}

interface Props {
	authData: AuthData;
	errorData: ErrorData;
}

class CancellationProvider extends React.Component<Props, CancellationData> {
	constructor(props: any, context: any) {
		super(props);
		this.state = {
			cancellationUpdateObj: {},
			reinstatementUpdateObj: {},
			policy: null,
			productConfig: null,
			selectedCancellation: null,
			setCancellationUpdateObj: this.setCancellationUpdateObj,
			setSelectedCancellation: this.setSelectedCancellation,
			setGraceLapse: this.setGraceLapse,
			setReinstatementUpdateObj: this.setReinstatementUpdateObj,
			setPolicy: this.setPolicy,
			setProductConfig: this.setProductConfig,
			loadData: this.loadData,
			loadCancellation: this.loadCancellation,
			clear: this.clear,
			isReinstatementUpdateObjValid: this.isReinstatementUpdateObjValid,
		};
	}

	public render() {
		return (
			<CancellationContext.Provider value={this.state}>
				{this.props.children}
			</CancellationContext.Provider>
		);
	}

	private setCancellationUpdateObj = (
		cancellationUpdateObj: CancellationUpdateRequest,
	) => {
		const promise = new Promise<CancellationData>((resolve, reject) => {
			this.setState({ cancellationUpdateObj }, () => {
				resolve(this.state);
			});
		});
		return promise;
	};

	private setSelectedCancellation = (
		selectedCancellation: CancellationResponse,
	) => {
		const promise = new Promise<CancellationData>((resolve, reject) => {
			this.setState({ selectedCancellation }, () => {
				resolve(this.state);
			});
		});
		return promise;
	};

	private setGraceLapse = (graceLapse: GraceLapseReinstatementResponse[]) => {
		const promise = new Promise<CancellationData>((resolve, reject) => {
			this.setState({ graceLapse }, () => {
				resolve(this.state);
			});
		});
		return promise;
	};

	private setReinstatementUpdateObj = (
		reinstatementUpdateObj: ReinstatementUpdateRequest,
	) => {
		const promise = new Promise<CancellationData>((resolve) => {
			this.setState({ reinstatementUpdateObj }, () => {
				resolve(this.state);
			});
		});
		return promise;
	};

	private setPolicy = (policy: Policy2) => {
		const promise = new Promise<CancellationData>((resolve, reject) => {
			this.setState({ policy }, () => {
				resolve(this.state);
			});
		});
		return promise;
	};

	private setProductConfig = (productConfig: ProductConfigurationResponse) => {
		const promise = new Promise<CancellationData>((resolve, reject) => {
			this.setState({ productConfig }, () => {
				resolve(this.state);
			});
		});
		return promise;
	};

	private loadCancellation = async (
		cancellationId: string,
		policyId: string,
		config?: AxiosRequestConfig<any>,
	) => {
		const { authData, errorData } = this.props;

		if (!authData?.auth) {
			return false;
		}

		try {
			const cancellationResponse = await CancellationsApi.getCancellation(
				authData.auth.authorizationToken,
				cancellationId,
				config,
			);

			this.setSelectedCancellation(cancellationResponse.data);

			return true;
		} catch (error) {
			if (axios.isCancel(error)) {
				throw error;
			} else {
				if (await handleUnauthorizedResponse(error, authData)) {
					return false;
				}
				errorData.handleErrorResponse(
					error,
					'Failed to fetch cancellation data',
				);

				return false;
			}
		}
	};

	private loadData = async (
		policyId: string,
		cancellationId: string,
		config?: AxiosRequestConfig<any>,
	) => {
		const { authData } = this.props;

		if (!authData?.auth) {
			return false;
		}

		try {
			const policyResponse = await PolicyApi.getPolicy(
				authData.auth.authorizationToken,
				policyId,
				config,
			);
			const policy = policyResponse.data as Policy2;
			const policyVersion = getPolicyConfigVersion(policy);
			const pvEnabled =
				authData.auth.configProperties[
					'property.product.versioning.m2.enabled'
				] ||
				authData.auth.configProperties[
					'property.product.versioning.with.repair.enabled'
				];
			let configs = null;
			if (pvEnabled) {
				configs = await ProductApi.getProductsWithVersion(
					authData.auth.authorizationToken,
					policyVersion,
					config,
				);
			} else {
				configs = await ProductApi.getProducts(
					authData.auth.authorizationToken,
					config,
				);
			}
			const productConfig = configs.data.find(
				(config: ProductConfigurationResponse) =>
					config.name === policy.productName,
			);

			await this.setPolicy(policy);
			await this.setProductConfig(productConfig);
			// Load grace lapse
			const graceAndLapse = await PolicyApi.getGraceLapseAndReinstatementInfo(
				authData.auth.authorizationToken,
				policyId,
				config,
			);
			await this.setGraceLapse(graceAndLapse.data);
			await this.loadCancellation(cancellationId, policyId, config);

			return true;
		} catch (error) {
			if (axios.isCancel(error)) {
				throw error;
			} else {
				if (await handleUnauthorizedResponse(error, authData)) {
					return false;
				}

				errorToast('Failed to fetch necessary data');
				return false;
			}
		}
	};

	private isReinstatementUpdateObjValid = () => {
		const { reinstatementUpdateObj, selectedCancellation } = this.state;
		const { reinstatement } = selectedCancellation;

		// only required if reinstatementDeadlineTimestamp is initially set
		if (
			!!reinstatement.reinstatementDeadlineTimestamp &&
			reinstatementUpdateObj.hasOwnProperty('reinstatementDeadlineTimestamp') &&
			!reinstatementUpdateObj.reinstatementDeadlineTimestamp
		) {
			return false;
		}

		return true;
	};

	private clear = () => {
		const promise = new Promise<CancellationData>((resolve) => {
			this.setState({}, () => {
				resolve(this.state);
			});
		});
		return promise;
	};
}

function withContext(Component: any) {
	return (props: Partial<Props>) => (
		<AuthContext.Consumer>
			{(authData) => (
				<ErrorContext.Consumer>
					{(errorData) => (
						<Component {...props} authData={authData} errorData={errorData} />
					)}
				</ErrorContext.Consumer>
			)}
		</AuthContext.Consumer>
	);
}

export const getReinstatementErrors = (
	reinstatement: ReinstatementResponse,
	deadlineDate: Date,
	minDate: Date,
	cannotBeBlank: boolean,
	authData: AuthData,
) => {
	if (reinstatement.reinstatementDeadlineTimestamp && deadlineDate === null) {
		return { isValid: false, message: "Can't be blank" };
	} else {
		return getDateErrors(deadlineDate, minDate, null, cannotBeBlank, authData);
	}
};

export default withContext(CancellationProvider);
